import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Checkbox, Tabs } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/layout/icons';
import { MONO_STYLE } from './class-bar';
import type { ClassChoice } from './metrics-math';
import { isoWeekKey, PERIOD_SCALES, periodDays, type Period, type PeriodScale } from './period';
import { TOKEN_CLASS_LABEL, TOKEN_CLASSES } from './pricing';

const DAY_MS = 86_400_000;

const SCALE_KEY: Readonly<Record<PeriodScale, string>> = {
  day: 'scale-day',
  week: 'scale-week',
  month: 'scale-month',
  thirteenWeeks: 'scale-thirteen-weeks',
};

/** ISO weekday of a civil date: 1 (Monday) … 7 (Sunday). */
export function isoWeekday(civil: Date): number {
  return ((civil.getUTCDay() + 6) % 7) + 1;
}

function isoWeekNumber(key: string): number {
  return Number(key.slice(-2));
}

/** `KW 38` + `14.09.–20.09.2026`, and the like for every scale. */
export function usePeriodLabel(period: Period): { readonly title: string; readonly range: string } {
  const intl = useIntl();
  const t = useTranslations('pages.admin.fleet-metrics');
  const { first, end } = periodDays(period);
  const last = new Date(end.getTime() - DAY_MS);
  const short = (civil: Date) => intl.formatDate(civil, { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
  const long = (civil: Date) =>
    intl.formatDate(civil, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
  const range = t('range', { from: short(first), to: long(last) });

  switch (period.scale) {
    case 'day':
      return { title: t('label-day', { weekday: t(`weekday-${isoWeekday(first)}`), date: long(first) }), range: '' };
    case 'week':
      return { title: t('label-week', { week: isoWeekNumber(period.key) }), range };
    case 'month':
      return { title: intl.formatDate(first, { month: 'long', year: 'numeric', timeZone: 'UTC' }), range };
    case 'thirteenWeeks': {
      const firstWeek = isoWeekNumber(isoWeekKey(first));

      return { title: t('label-thirteen-weeks', { first: firstWeek, last: isoWeekNumber(period.key) }), range };
    }
  }
}

export function PeriodPicker({
  period,
  canPrevious,
  canNext,
  compact,
  onScale,
  onStep,
}: {
  readonly period: Period;
  readonly canPrevious: boolean;
  readonly canNext: boolean;
  readonly compact: boolean;
  readonly onScale: (scale: PeriodScale) => void;
  readonly onStep: (steps: number) => void;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const label = usePeriodLabel(period);

  return (
    <div className="flex min-w-0 flex-wrap items-center" style={{ gap: compact ? 'var(--space-3)' : 'var(--space-4)' }}>
      <Tabs<PeriodScale>
        idBase="fleet-metrics-scale"
        size="sm"
        tabs={PERIOD_SCALES.map((scale) => ({ key: scale, label: t(SCALE_KEY[scale]) }))}
        active={period.scale}
        onChange={onScale}
      />
      <span className="inline-flex min-w-0 items-center" style={{ gap: 'var(--space-2)' }}>
        <Button
          variant="secondary"
          size="sm"
          aria-label={t('previous')}
          disabled={!canPrevious}
          onClick={() => onStep(-1)}
          style={{ width: 28, height: 28, padding: 0 }}
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
        </Button>
        <span
          className="inline-flex min-w-0"
          style={{
            flexDirection: compact ? 'column' : 'row',
            alignItems: compact ? 'flex-start' : 'baseline',
            gap: compact ? 0 : 'var(--space-2)',
          }}
        >
          <span
            data-testid="period-label"
            style={{
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--font-weight-semibold)',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}
          >
            {label.title}
          </span>
          {label.range !== '' && (
            <span style={{ ...MONO_STYLE, fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
              {label.range}
            </span>
          )}
        </span>
        <Button
          variant="secondary"
          size="sm"
          aria-label={t('next')}
          disabled={!canNext}
          onClick={() => onStep(1)}
          style={{ width: 28, height: 28, padding: 0 }}
        >
          <ChevronRightIcon className="h-4 w-4" aria-hidden />
        </Button>
      </span>
    </div>
  );
}

/**
 * The class choice as a segmented control. The kit has none; it is composed
 * from kit buttons, one pressed at a time.
 */
export function ClassPicker({
  value,
  onChange,
}: {
  readonly value: ClassChoice;
  readonly onChange: (value: ClassChoice) => void;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const options: ReadonlyArray<{ readonly key: ClassChoice; readonly label: string }> = [
    { key: 'all', label: t('class-all') },
    ...TOKEN_CLASSES.map((key) => ({ key, label: TOKEN_CLASS_LABEL[key] })),
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t('filter-class')}
      className="inline-flex flex-wrap"
      style={{
        padding: 2,
        gap: 2,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--color-neutral-200)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {options.map((option) => {
        const on = option.key === value;

        return (
          <Button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={on}
            variant="ghost"
            size="sm"
            onClick={() => onChange(option.key)}
            style={{
              height: 26,
              padding: '0 var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              background: on ? 'var(--bg-surface)' : 'transparent',
              boxShadow: on ? 'var(--shadow-sm)' : 'none',
              color: on ? 'var(--text-primary)' : 'var(--text-tertiary)',
              fontFamily: option.key === 'all' ? 'var(--font-sans)' : 'var(--font-mono)',
              fontSize: option.key === 'all' ? 'var(--text-base-sm)' : 'var(--text-sm)',
              fontWeight: on ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)',
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * Multi-select over the agents, default all. The button names the selection —
 * a filter that silently drops data would be a lie — and the last box cannot
 * be cleared. Run counts are the period's, unfiltered, so an operator sees
 * what they are deselecting.
 */
export function AgentFilter({
  agents,
  selected,
  runsByAgent,
  onChange,
}: {
  readonly agents: ReadonlyArray<string>;
  readonly selected: ReadonlyArray<string>;
  readonly runsByAgent: ReadonlyMap<string, number>;
  readonly onChange: (next: ReadonlyArray<string>) => void;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const all = agents.every((agent) => selected.includes(agent));
  const label = all
    ? t('agents-all')
    : selected.length <= 2
      ? selected.join(', ')
      : t('agents-some', { count: selected.length, total: agents.length });

  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };

    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);

    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const toggle = (agent: string) => {
    const next = selected.includes(agent)
      ? selected.filter((entry) => entry !== agent)
      : agents.filter((entry) => entry === agent || selected.includes(entry));

    if (next.length > 0) onChange(next);
  };

  return (
    <div ref={root} className="relative flex-none">
      <Button
        variant="secondary"
        size="sm"
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="agent-filter"
        onClick={() => setOpen(!open)}
      >
        <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{label}</span>
        <ChevronDownIcon className="h-3 w-3" aria-hidden style={{ color: 'var(--text-tertiary)' }} />
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label={t('filter-agents')}
          data-testid="agent-popover"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 20,
            width: 260,
            background: 'var(--bg-surface)',
            border: '1px solid var(--color-neutral-200)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            padding: 'var(--space-2)',
          }}
        >
          <label
            className="flex cursor-pointer items-center"
            style={{
              gap: 'var(--space-2)',
              padding: '6px var(--space-2)',
              borderBottom: '1px solid var(--color-neutral-100)',
              marginBottom: 'var(--space-1)',
            }}
          >
            <Checkbox
              checked={all ? true : 'indeterminate'}
              onCheckedChange={() => onChange(agents)}
              aria-label={t('agents-all')}
            />
            <span style={{ fontSize: 'var(--text-base-sm)', fontWeight: 'var(--font-weight-medium)' }}>
              {t('agents-all')}
            </span>
          </label>
          {agents.map((agent) => (
            <label
              key={agent}
              className="flex cursor-pointer items-center"
              style={{ gap: 'var(--space-2)', padding: '6px var(--space-2)', borderRadius: 'var(--radius-sm)' }}
            >
              <Checkbox checked={selected.includes(agent)} onCheckedChange={() => toggle(agent)} aria-label={agent} />
              <span style={{ ...MONO_STYLE, fontSize: 'var(--text-base-sm)', flex: 1 }}>{agent}</span>
              <span style={{ ...MONO_STYLE, fontSize: 'var(--text-sm)', color: 'var(--text-placeholder)' }}>
                {t('agents-runs', { count: runsByAgent.get(agent) ?? 0 })}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
